import React from "react";
import { CategoryTabType, FilterComponentsType } from "@scenes/explore/types";
import { ProductCategory } from "@utils/constants";
import { LayoutChangeEvent } from "react-native";
import SectionContent from "@components/atoms/SectionContent";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";

export const generateSections = (filters: CategoryTabType[], t: any) => {
  return filters?.map((section) => ({
    key: section.name,
    title: t(section.label),
    isCollection: !!section.collection,
  }));
};

export const mergeFiltersWithSameName = (
  filters: any[],
  selectedCollectionNames?: string[]
) => {
  const checkCollectionDependencies = (
    collectionDeps: string[] | undefined,
    currentCollections: string[] | null | undefined
  ): boolean => {
    if (!collectionDeps || collectionDeps.length === 0) {
      return true;
    }

    if (
      !currentCollections ||
      !Array.isArray(currentCollections) ||
      currentCollections.length === 0
    ) {
      return false;
    }

    return collectionDeps.some((dep) =>
      currentCollections.some(
        (collection) => collection === dep || collection.includes(dep)
      )
    );
  };

  const filterMap = new Map<string, any>();

  filters.forEach((filter) => {
    const filterName = filter.name;

    const dependenciesMet = checkCollectionDependencies(
      filter.collection_dependencies,
      selectedCollectionNames
    );

    if (!dependenciesMet) {
      // Skip this filter if dependencies are not met
      return;
    }

    if (filterMap.has(filterName)) {
      const existingFilter = filterMap.get(filterName);
      const mergedOptions = [
        ...(existingFilter.options || []),
        ...(filter.options || []),
      ];

      const uniqueOptions = mergedOptions.filter(
        (option, index, arr) =>
          arr.findIndex((o) => o.name === option.name) === index
      );

      const existingCollectionDeps =
        existingFilter.collection_dependencies || [];
      const newCollectionDeps = filter.collection_dependencies || [];
      const mergedCollectionDeps = [
        ...new Set([...existingCollectionDeps, ...newCollectionDeps]),
      ];

      filterMap.set(filterName, {
        ...existingFilter,
        options: uniqueOptions,
        collection_dependencies:
          mergedCollectionDeps.length > 0 ? mergedCollectionDeps : undefined,

        _mergeId: `merged-${filterName}`,
      });
    } else {
      filterMap.set(filterName, {
        ...filter,
        _mergeId: `single-${filter.name}`,
      });
    }
  });

  return Array.from(filterMap.values());
};

export const combineFiltersByWebWidth = (mFilters: any[]) => {
  const resultantFilters: any[] = [];
  let tempCombination: { filters: any[]; webWidth: number } | null = null;

  mFilters.forEach((filter, index) => {
    const currentWidth = filter.web_width || 100;

    if (tempCombination) {
      const combinedWidth = tempCombination.webWidth + currentWidth;

      if (combinedWidth <= 100) {
        tempCombination.filters.push(filter);
        tempCombination.webWidth = combinedWidth;
      } else {
        resultantFilters.push(tempCombination);
        tempCombination = { filters: [filter], webWidth: currentWidth };
      }
    } else {
      tempCombination = { filters: [filter], webWidth: currentWidth };
    }

    if (index === mFilters.length - 1 && tempCombination) {
      resultantFilters.push(tempCombination);
    }
  });

  return resultantFilters;
};

export const generateSectionsComponents = (
  filters: CategoryTabType[],
  theme: any,
  productType: ProductCategory,
  fromResultScreen: boolean,
  filterContainerWidth?: number,
  setContainerWidth?: (value: number) => void,
  isOnSplitScreen?: boolean,
  showScrollbars?: boolean,
  enterSecondFlow?: boolean,
  _selectedCollectionNames?: string[],
  headerComponent?: React.ReactNode,
  sizeToContent?: boolean
) => {
  const scenes: FilterComponentsType = {};
  const handleLayout = (e: LayoutChangeEvent) => {
    let value = Math.max(
      0,
      e.nativeEvent.layout.width -
        (e.nativeEvent.layout.width < 1008 && e.nativeEvent.layout.height < 500
          ? 43
          : 32) -
        (fromResultScreen ? 4 : 0)
    );
    if (value !== 0) {
      setContainerWidth?.(value);
    }
  };

  filters?.forEach((section) => {
    const {
      name: sectionName,
      filters: sectionFilters,
      isCollection,
    } = section;

    const { normalFilters } = sectionFilters.reduce(
      (acc, filter) => {
        if (filter.name) {
          acc.normalFilters.push(filter);
        }
        return acc;
      },
      { normalFilters: [] }
    );

    const scrollViewStyles = {
      ...(theme.isMobile || fromResultScreen ? {} : { alignItems: "center" }),
      paddingBottom: 40,
    };

    scenes[sectionName] = () => (
      <SectionContent
        scrollViewStyles={scrollViewStyles}
        handleLayout={handleLayout}
        rawFilters={normalFilters}
        isCollection={!!isCollection}
        theme={theme}
        fromResultScreen={fromResultScreen}
        sectionName={sectionName}
        productType={productType}
        filterContainerWidth={filterContainerWidth}
        isOnSplitScreen={isOnSplitScreen}
        showScrollbars={showScrollbars}
        enterSecondFlow={normalizeBoolean(enterSecondFlow)}
        headerComponent={headerComponent}
        sizeToContent={sizeToContent}
      />
    );
  });

  return scenes;
};

const sortByOrder = (arr: any) => {
  return arr.sort((a: any, b: any) => {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    return 0;
  });
};

export const filterSearchFilters = (obj: unknown, targetPriceModes: any) => {
  if (Array.isArray(obj)) {
    return obj.reduce((filteredArr, item) => {
      const filtered = filterSearchFilters(item, targetPriceModes);
      if (
        filtered !== null &&
        (Array.isArray(filtered)
          ? filtered.length > 0
          : Object.keys(filtered).length > 0)
      ) {
        filteredArr.push(filtered);
      }

      return sortByOrder(filteredArr);
    }, []);
  } else if (typeof obj === "object" && obj !== null) {
    const filteredObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === "disabled" && value === true) {
        return null;
      } else if (key === "price_modes") {
        if (value.includes(targetPriceModes)) {
          filteredObj[key] = value;
        } else {
          return null;
        }
      } else if (
        key === "filters" ||
        key === "sub_filters" ||
        key === "options"
      ) {
        const filtered = filterSearchFilters(value, targetPriceModes);
        if (filtered !== null && filtered.length > 0) {
          filteredObj[key] = filtered;
        }
      } else {
        if (value !== null) {
          filteredObj[key] = value;
        }
      }
    }
    return Object.keys(filteredObj).length > 0 ? filteredObj : null;
  }
  return obj;
};
